import { format } from 'date-fns';
import { de, fr } from 'date-fns/locale';

export type QuestionType = 'rating' | 'open';

/**
 * S5-03 — `id` est stable (jamais régénéré) et sert uniquement de trackBy
 * d'affichage et de cible pour `sectionId`. Le classement par section est
 * purement présentationnel : la réponse (`QuestionnaireResponse.ratings`/
 * `.answers`) continue d'indexer par position dans `Questionnaire.questions`
 * (le tableau canonique, jamais celui, trié par section, utilisé à
 * l'affichage) — voir libs/questionnaire/sections.ts. Aucun risque de
 * réattribution de note par ce classement, quel que soit l'ordre
 * d'affichage choisi.
 *
 * Un déplacement/suppression de question modifierait quand même les index
 * existants si elle survenait après envoi de réponses — mais l'éditeur admin
 * verrouille déjà `questions` dès la publication (aucune modification,
 * ajout ou suppression possible une fois publié, voir
 * edit-questionnaire.component.html) et aucune réponse n'existe avant
 * publication : ce risque n'a donc pas de chemin réel dans l'application
 * actuelle, constaté en explorant ce ticket.
 */
export type Question = {
  id: string;
  type: QuestionType;
  question: string;
  order: number;
  rating?: number;
  answer?: string;
  sectionId?: string;
};

/** S5-03 — regroupement organisationnel des questions, titre et ordre propres. */
export type Section = {
  id: string;
  title: string;
  order: number;
};

/**
 * Association questionnaire-formation. `activeUntil` (délai de réponse,
 * préexistant) n'est pas redéfini par S5-01. `openAt`/`openAtTimeZone`
 * (S5-01) sont indépendants : `openAt` est un instant UTC absolu, jamais une
 * heure murale — voir libs/questionnaire/scheduling.ts. Absent = toujours
 * ouvert (compat S5-05).
 */
export type QuestionnaireTraining = {
  id: string;
  activeUntil?: string;
  openAt?: string;
  openAtTimeZone?: string;
};

/**
 * Une réponse (bilan ou évaluation) : un élément de `user.results`/
 * `user.evaluations` (libs/user/user.model.ts). Pas une collection Firestore
 * à part — voir UserService.addQuestionnaireResponse pour l'écriture.
 */
export type QuestionnaireResponse = {
  id: string;
  questionnaireId: string;
  tutorId?: string;
  managerId?: string;
  trainingId?: string;
  comments?: string;
  rdvRequest?: boolean;
  level?: number;
  averageNote?: number;
  sendingDate?: string;
  ratings?: Array<{ index: number; rating: number; maxRating?: number }>;
  answers?: Array<{ index: number; answer: string }>;
};

/**
 * Identifiant de tentative — pas un UUID crypto (compat WebView anciennes,
 * cf. audit ergonomique E-36/E-39), juste assez unique pour cibler une
 * réponse précisément (S4-02 : « identifier sans ambiguïté... tentative »,
 * prépare le retrait ciblé d'une réponse en S4-03).
 */
export function generateResponseId(): string {
  return `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;
}

/** S5-03 — même schéma que `generateResponseId`, pour les ids stables de questions/sections. */
export function generateStructureId(): string {
  return `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;
}

/**
 * S5-05 — compat : les questions d'un questionnaire antérieur à S5-03 n'ont
 * pas d'`id` persisté. Un id dérivé de la position suffit : `questions` est
 * déjà verrouillé dès la publication (voir le commentaire sur `Question`),
 * donc cette position ne varie plus une fois qu'une réponse existe.
 */
function legacyQuestionId(index: number): string {
  return `legacy-${index}`;
}

export class Questionnaire {
  constructor(
    public title: string,
    public type: string,
    public userRole: string,
    public questions: Question[],
    public trainings: QuestionnaireTraining[],
    public trainingIds: string[],
    public isPublished: boolean,
    public trainingCenterId?: string,
    public id?: string,
    public sendingDate?: string,
    public evaluatorRole?: string,
    public description?: string,
    // Rangement admin — S1-03. Indépendant de isPublished : un questionnaire
    // archivé reste publié, répondu et exporté comme avant ; seule sa présence
    // dans la liste/les sous-menus admin change. Absent sur un document
    // ancien = non archivé (cf. fromObject).
    public isArchived?: boolean,
    // S5-03 — sections ordonnées. Absent/vide sur un document ancien ou sans
    // section définie (compat S5-05) : toutes les questions s'affichent sans
    // en-tête de section, comme avant ce ticket.
    public sections?: Section[],
  ) {}

  static fromObject(data: any): Questionnaire {
    return new Questionnaire(
      data?.title,
      data?.type,
      data?.userRole,
      data?.questions?.map((q: any, index: number) => ({
        id: q.id ?? legacyQuestionId(index),
        type: q.type ?? 'rating',
        question: q.question ?? '',
        order: q.order ?? 1,
        rating: q.type === 'rating' || (!q.type && q.rating) ? (q.rating ?? 5) : undefined,
        answer: q.type === 'open' ? (q.answer ?? '') : undefined,
        sectionId: q.sectionId,
      })) ?? [],
      data?.trainings ?? [],
      data?.trainingIds ?? [],
      data?.isPublished ?? false,
      data?.trainingCenterId,
      data?.id,
      data?.sendingDate,
      data?.evaluatorRole,
      data?.description,
      data?.isArchived ?? false,
      data?.sections ?? [],
    );
  }

  static toObject(questionnaire: Questionnaire): any {
    const data: any = {
      title: questionnaire.title,
      type: questionnaire.type,
      userRole: questionnaire.userRole,
      questions: questionnaire.questions.map((q) => {
        const question: any = {
          id: q.id ?? generateStructureId(),
          type: q.type,
          question: q.question,
          order: q.order,
        };

        if (q.type === 'rating' && q.rating !== undefined) {
          question.rating = q.rating;
        }
        if (q.type === 'open' && q.answer !== undefined) {
          question.answer = q.answer;
        }
        if (q.sectionId !== undefined) {
          question.sectionId = q.sectionId;
        }

        return question;
      }),
      trainings: questionnaire.trainings,
      trainingIds: questionnaire.trainings ? questionnaire.trainings.map((t) => t.id) : [],
      isPublished: questionnaire.isPublished,
      trainingCenterId: questionnaire.trainingCenterId,
      // S5-03 — toujours présent (défaut []), même convention que questions/trainings.
      sections: questionnaire.sections ?? [],
    };

    if (questionnaire.evaluatorRole !== undefined) {
      data.evaluatorRole = questionnaire.evaluatorRole;
    }

    if (questionnaire.description !== undefined) {
      data.description = questionnaire.description;
    }

    // Omis si non défini plutôt qu'écrit à `undefined` (Firestore refuserait
    // l'écriture) : une sauvegarde normale de l'éditeur, qui ne connaît pas ce
    // champ pour un document ancien, ne touche donc jamais au rangement.
    if (questionnaire.isArchived !== undefined) {
      data.isArchived = questionnaire.isArchived;
    }

    return data;
  }

  static getDisplayFields(): any[] {
    const displayFields: any[] = [
      { name: 'title', header: 'Titre', size: 4 },
      {
        name: 'isPublished',
        header: 'Publié ?',
        transform: (val: boolean) => (val === true ? 'publié' : 'brouillon'),
        size: 2,
      },
    ];

    return displayFields;
  }

  static getQuestionDisplayFields(): any[] {
    const displayFields: any[] = [
      { name: 'order', header: '#', size: 1 },
      { name: 'question', header: 'Question', size: 8 },
      { name: 'rating', header: 'Note max.', size: 1 },
    ];

    return displayFields;
  }

  /** S5-03 */
  static getSectionDisplayFields(): any[] {
    const displayFields: any[] = [
      { name: 'order', header: '#', size: 1 },
      { name: 'title', header: 'Titre', size: 9 },
    ];

    return displayFields;
  }

  static getTrainingDisplayFields(): any[] {
    const displayFields: any[] = [
      { name: 'title', header: 'Titre', size: 4 },
      {
        name: 'activeUntil',
        header: `Réponses acceptées jusqu'au`,
        transform: (val: string) => format(new Date(val), 'dd MMMM yyyy', { locale: fr }),
        type: 'date',
        size: 4,
      },
    ];

    return displayFields;
  }
}
